import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, Chip, Link } from "@mui/material";
import { useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { ResourceName } from "../../constants/resources";
import { CrmFieldType, CrmObject, type CrmValueFieldsFragment } from "../../generated/graphql";
import { useCrmLabels } from "../../hooks/useCrmLabels";
import { CRM_COLORS, crmRecordPath, formatCrmValue, isMarketingObject, splitMultiPicklist } from "../../lib/crm";
import { PhoneNumberLink } from "../telephony/PhoneNumberLink";
import { CrmConsentBadges } from "./CrmConsentBadges";

type CrmValueViewProps = {
  value: CrmValueFieldsFragment;
  // Long text keeps its line breaks in record details, not in table cells.
  multiline?: boolean;
};

const linkSx = { color: CRM_COLORS.link, textDecoration: "none", "&:hover": { textDecoration: "underline" } };

const RecordLink = ({ object, id, title }: { object: CrmObject; id: string; title: string }) => (
  <Link component={RouterLink} to={crmRecordPath(object, id)} sx={linkSx}>
    {title}
  </Link>
);

// Staff without Marketing see a campaign's name without a link to its page.
const MarketingRecordLink = (props: { object: CrmObject; id: string; title: string }) => {
  const { canAccess } = useCanAccess({ action: "list", resource: ResourceName.Marketing });
  return canAccess === true ? <RecordLink {...props} /> : <>{props.title}</>;
};

const ObjectName = ({ object }: { object: string }) => {
  const labels = useCrmLabels();
  return <>{labels.object(object as CrmObject)}</>;
};

export const CrmValueView = ({ value, multiline = false }: CrmValueViewProps) => {
  const translate = useTranslate();
  const raw = value.value;
  // Empty, or a field the integration user may not read.
  if (raw === null || raw === undefined || raw === "") return null;

  if (value.type === CrmFieldType.Boolean) {
    const checked = raw === "true";
    const Icon = checked ? CheckBoxOutlinedIcon : CheckBoxOutlineBlankIcon;
    return (
      <Icon
        titleAccess={translate(checked ? "crm.record.yes" : "crm.record.no")}
        sx={{ fontSize: 18, color: checked ? CRM_COLORS.text : CRM_COLORS.label, verticalAlign: "middle" }}
      />
    );
  }

  switch (value.type) {
    case CrmFieldType.Reference: {
      // The copy deletes a person account Salesforce deleted, while the
      // business records it kept still name it.
      if (value.referenceObject === CrmObject.Account && !value.referenceTitle) {
        return (
          <Box component="span" title={raw} sx={{ color: CRM_COLORS.label }}>
            {translate("crm.accountDeleted")}
          </Box>
        );
      }
      const title = value.referenceTitle || raw;
      if (!value.referenceObject) return <>{title}</>;
      const Reference = isMarketingObject(value.referenceObject) ? MarketingRecordLink : RecordLink;
      return <Reference object={value.referenceObject} id={raw} title={title} />;
    }
    case CrmFieldType.Object:
      return <ObjectName object={raw} />;
    case CrmFieldType.Email:
      return (
        <Link href={`mailto:${raw}`} sx={linkSx} title={translate("crm.record.email", { address: raw })}>
          {raw}
        </Link>
      );
    case CrmFieldType.Phone:
      return (
        <PhoneNumberLink
          number={raw}
          sx={{ ...linkSx, display: "inline-flex", alignItems: "center", gap: "4px" }}
          aria-label={translate("crm.record.call", { number: raw })}
        >
          <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
          {raw}
        </PhoneNumberLink>
      );
    case CrmFieldType.Url:
      // Salesforce keeps websites as typed, often without a scheme.
      return (
        <Link
          href={/^https?:\/\//i.test(raw) ? raw : `https://${raw}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={linkSx}
        >
          {raw.replace(/^https?:\/\//, "")}
        </Link>
      );
    case CrmFieldType.MultiPicklist:
      return (
        <Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", gap: "4px" }}>
          {splitMultiPicklist(raw).map(entry => (
            <Chip key={entry} label={entry} size="small" sx={{ height: 20, fontSize: "12px" }} />
          ))}
        </Box>
      );
    case CrmFieldType.Consent:
      return <CrmConsentBadges value={raw} />;
    case CrmFieldType.LongText:
      return (
        <Box component="span" sx={multiline ? { whiteSpace: "pre-wrap" } : undefined}>
          {raw}
        </Box>
      );
    default:
      return <>{formatCrmValue(value.type, raw)}</>;
  }
};
